import React, { useState, useEffect, useMemo } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Spinner,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Input,
    FormGroup,
    Label,
} from 'reactstrap';
import { getData, postData } from '../../../CommonElements/Functions/PostGetFun';
import { API } from '../../../api/API';
import { toast } from 'react-toastify';
import moment from 'moment';
import FeatherIconCom from '../../../Utils/CommonComponents/CommonIcons/FeatherIconCom';
import Loader from '../../../Layout/Loader';
import { isOwnRequest } from '../../../Utils/authUtils';

export default function HolidayApprovals() {
    const serverUrl = localStorage.getItem("serverUrl");
    const [loading, setLoading] = useState<boolean>(true);
    const [requests, setRequests] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('all');

    const [selectedRequest, setSelectedRequest] = useState<any>(null);
    const [detailModalOpen, setDetailModalOpen] = useState<boolean>(false);

    // Modal state for Approve / Reject
    const [actionModalType, setActionModalType] = useState<'approve' | 'reject' | null>(null);
    const [targetReqItem, setTargetReqItem] = useState<any>(null);
    const [rejectRemark, setRejectRemark] = useState<string>('');
    const [submittingAction, setSubmittingAction] = useState<boolean>(false);

    const getDayColor = (dayName: string) => {
        switch ((dayName || '').toLowerCase()) {
            case 'monday': return '#0D47A1';
            case 'tuesday': return '#1976D2';
            case 'wednesday': return '#00ACC1';
            case 'thursday': return '#00897B';
            case 'friday': return '#0288D1';
            case 'saturday': return '#7E57C2';
            case 'sunday': return '#E53935';
            default: return '#6c757d';
        }
    };

    const fetchRequests = async () => {
        if (!serverUrl) return;
        setLoading(true);
        try {
            const url = `${serverUrl}${API.getHolidayRequests}`;
            const response = await getData(url, true, { module_name: 'Holiday' });
            console.log("Holiday requests response:", response);

            if (response.status === 200 || response.data?.statusCode === 200) {
                let list: any[] = [];
                const d = response.data;
                if (Array.isArray(d)) list = d;
                else if (Array.isArray(d?.data)) list = d.data;
                else if (Array.isArray(d?.items)) list = d.items;
                else if (d && typeof d === 'object' && !Array.isArray(d)) list = [d];

                setRequests(list);
            } else {
                setRequests([]);
            }
        } catch (error) {
            console.error("Error fetching holiday requests:", error);
            toast.error("Failed to fetch holiday requests");
            setRequests([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
    }, [serverUrl]);

    const handleOpenActionModal = (reqItem: any, type: 'approve' | 'reject') => {
        setTargetReqItem(reqItem);
        setActionModalType(type);
        setRejectRemark('');
    };

    const handleConfirmAction = async () => {
        if (!targetReqItem || !actionModalType || !serverUrl) return;

        if (actionModalType === 'reject' && !rejectRemark.trim()) {
            toast.error("Please enter a rejection remark");
            return;
        }

        const holidayReqId = targetReqItem.holiday_req_id ??
            targetReqItem.holidays?.map((h: any) => h.HolidayId || h.holiday_id).filter(Boolean).join(',') ??
            targetReqItem.id;

        setSubmittingAction(true);
        try {
            const url = `${serverUrl}${API.actionHolidayRequest}`;
            const payload: any = {
                module_name: 'Holiday',
                holiday_req_id: holidayReqId,
                status: actionModalType === 'approve' ? 'approved' : 'rejected',
            };
            if (actionModalType === 'reject') {
                payload.reject_remark = rejectRemark.trim();
            }

            const response = await postData(url, true, payload);
            const ok = response.status === 200 || response.data?.statusCode === 200;

            if (ok) {
                toast.success(response.data?.message || `Holiday request ${actionModalType === 'approve' ? 'approved' : 'rejected'} successfully!`);
                setActionModalType(null);
                setTargetReqItem(null);
                setDetailModalOpen(false);
                fetchRequests();
            } else {
                toast.error(response.data?.message || response.data?.error || `Failed to ${actionModalType} holiday request`);
            }
        } catch (error: any) {
            console.error("Holiday action error:", error);
            toast.error(error?.message || "An error occurred while performing action");
        } finally {
            setSubmittingAction(false);
        }
    };

    const getOverallStatus = (item: any) => {
        if (item.status) return item.status;
        if (item.Status) return item.Status;
        if (Array.isArray(item.holidays) && item.holidays.length > 0) {
            return item.holidays[0].Status || item.holidays[0].status || 'pending';
        }
        return 'pending';
    };

    const getStatusBadgeColor = (status: string) => {
        switch ((status || '').toLowerCase()) {
            case 'approved': return 'success';
            case 'rejected': return 'danger';
            case 'pending': return 'warning';
            default: return 'primary';
        }
    };

    const filteredRequests = useMemo(() => {
        return requests.filter(item => {
            const status = getOverallStatus(item).toLowerCase();
            if (selectedStatusFilter !== 'all' && status !== selectedStatusFilter.toLowerCase()) {
                return false;
            }
            if (searchTerm.trim()) {
                const search = searchTerm.toLowerCase();
                const empName = (item.employee_name || item.name || item.EmployeeName || '').toLowerCase();
                const reqDate = (item.requested_date || item.created_at || '').toLowerCase();
                return empName.includes(search) || reqDate.includes(search);
            }
            return true;
        });
    }, [requests, searchTerm, selectedStatusFilter]);

    return (
        <div className="px-2 py-3" style={{ color: '#000000' }}>
            <Container fluid>
                <Col xxl={12}>
                    {/* Filter and Search Bar */}
                    <div className="d-flex justify-content-between align-items-center mb-4 py-2 gap-2 flex-wrap bg-white p-3 rounded border shadow-sm">
                        <div style={{ maxWidth: "320px", minWidth: "260px" }}>
                            <Input
                                type="text"
                                className="form-control text-dark border shadow-none"
                                placeholder="Search by employee name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <label className="form-label mb-0 small fw-bold text-dark me-1">Status:</label>
                            <Input
                                type="select"
                                className="form-select text-dark border shadow-none py-1.5 px-3 fw-semibold"
                                value={selectedStatusFilter}
                                onChange={(e) => setSelectedStatusFilter(e.target.value)}
                                style={{ width: "160px", fontSize: "0.9rem" }}
                            >
                                <option value="all">All Statuses</option>
                                <option value="pending">Pending</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                            </Input>

                            <Button
                                color="dark"
                                outline
                                size="sm"
                                className="d-flex align-items-center gap-1 ms-2"
                                onClick={fetchRequests}
                            >
                                <FeatherIconCom iconName="RefreshCw" size={14} />
                                <span>Refresh</span>
                            </Button>
                        </div>
                    </div>

                    {/* Content List */}
                    {loading ? (
                        <Loader height="250px" />
                    ) : filteredRequests.length > 0 ? (
                        <Row className="g-3">
                            {filteredRequests.map((item, idx) => {
                                const empName = item.employee_name || item.name || item.EmployeeName || 'Unknown Employee';
                                const requestedDate = item.requested_date || (item.created_at ? moment(item.created_at).format('YYYY-MM-DD') : '-');
                                const status = getOverallStatus(item);
                                const isPending = status.toLowerCase() === 'pending';
                                const holidaysList: any[] = item.holidays || item.items || [];

                                return (
                                    <Col lg={12} key={item.holiday_req_id || idx}>
                                        <Card className="border mb-3 shadow-sm bg-white" style={{ borderRadius: '12px', overflow: 'hidden', borderColor: '#cccccc' }}>
                                            {/* Card Header Info */}
                                            <div className="p-3 bg-light border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
                                                <div>
                                                    <h5 className="fw-bold text-dark mb-1" style={{ color: '#000000', fontSize: '1.1rem' }}>
                                                        {empName}
                                                    </h5>
                                                    <div className="text-dark small fw-semibold" style={{ color: '#000000' }}>
                                                        <span>Requested on: </span>
                                                        <span className="fw-normal">{requestedDate}</span>
                                                        <span className="mx-2">|</span>
                                                        <Badge color="info" className="px-2 py-1 fw-bold text-white">
                                                            {holidaysList.length} Optional {holidaysList.length === 1 ? 'Holiday' : 'Holidays'} Requested
                                                        </Badge>
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                                    <Badge color={getStatusBadgeColor(status)} className="px-3 py-1.5 fw-bold text-uppercase" style={{ fontSize: '0.8rem' }}>
                                                        {status}
                                                    </Badge>

                                                    {isPending && !isOwnRequest(item) && (
                                                        <>
                                                            <Button
                                                                color="danger"
                                                                size="sm"
                                                                className="px-3 py-1 fw-bold text-white me-1"
                                                                style={{ backgroundColor: '#EF5350', borderColor: '#EF5350' }}
                                                                onClick={() => handleOpenActionModal(item, 'reject')}
                                                            >
                                                                Reject
                                                            </Button>
                                                            <Button
                                                                color="success"
                                                                size="sm"
                                                                className="px-3 py-1 fw-bold text-white me-1"
                                                                style={{ backgroundColor: '#6cbb33', borderColor: '#6cbb33' }}
                                                                onClick={() => handleOpenActionModal(item, 'approve')}
                                                            >
                                                                Approve
                                                            </Button>
                                                        </>
                                                    )}

                                                    <Button
                                                        color="primary"
                                                        size="sm"
                                                        className="px-3 py-1 fw-bold text-white"
                                                        onClick={() => {
                                                            setSelectedRequest(item);
                                                            setDetailModalOpen(true);
                                                        }}
                                                    >
                                                        View Request
                                                    </Button>
                                                </div>
                                            </div>

                                            {/* Card Preview Grid of Holidays */}
                                            <CardBody className="p-3 bg-white" style={{ color: '#000000' }}>
                                                {holidaysList.length > 0 ? (
                                                    <Row className="g-3">
                                                        {holidaysList.map((h: any, hIdx: number) => {
                                                            const hDate = moment(h.HolidayDate || h.holiday_date, 'YYYY-MM-DD');
                                                            const dayName = hDate.isValid() ? hDate.format('dddd') : (h.Day || '');
                                                            const dateFormatted = hDate.isValid() ? hDate.format('D MMMM YYYY') : (h.HolidayDate || h.holiday_date);

                                                            return (
                                                                <Col md={6} lg={3} key={h.HolidayId || hIdx}>
                                                                    <div className="p-3 rounded border text-center bg-light h-100 d-flex flex-column align-items-center justify-content-center" style={{ borderColor: '#e0e0e0' }}>
                                                                        <div className="fw-bold text-dark mb-1" style={{ fontSize: '0.92rem', color: '#000000' }}>
                                                                            {dateFormatted}
                                                                        </div>
                                                                        {dayName && (
                                                                            <Badge
                                                                                className="px-2.5 py-1 mb-1.5 fw-bold text-white"
                                                                                style={{ backgroundColor: getDayColor(dayName), borderRadius: '5px', fontSize: '0.78rem' }}
                                                                            >
                                                                                {dayName}
                                                                            </Badge>
                                                                        )}
                                                                        <div className="fw-bold text-secondary small">
                                                                            {h.HolidayName || h.holiday_name || 'Optional Holiday'}
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                            );
                                                        })}
                                                    </Row>
                                                ) : (
                                                    <div className="text-muted small">No holiday items listed</div>
                                                )}
                                            </CardBody>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    ) : (
                        <div className="text-center py-5 bg-white rounded border">
                            <FeatherIconCom iconName="Sun" size={48} className="mb-3 text-secondary" />
                            <h5 className="fw-bold text-dark">No Holiday Requests Found</h5>
                            <p className="text-muted small mb-0">No optional holiday approval requests match your search or filter.</p>
                        </div>
                    )}
                </Col>
            </Container>

            {/* Detailed View Modal */}
            <Modal isOpen={detailModalOpen} toggle={() => setDetailModalOpen(false)} size="lg" centered>
                <ModalHeader toggle={() => setDetailModalOpen(false)} className="border-bottom pb-3">
                    <div>
                        <h5 className="fw-bold mb-0 text-dark">{selectedRequest?.employee_name || 'Employee Request Details'}</h5>
                        <small className="text-muted">{selectedRequest?.holidays?.length || 0} Optional Holidays Requested</small>
                    </div>
                </ModalHeader>
                <ModalBody className="p-4" style={{ color: '#000000' }}>
                    {selectedRequest && (
                        <>
                            <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                                <div>
                                    <span className="fw-bold text-dark">Status: </span>
                                    <Badge color={getStatusBadgeColor(getOverallStatus(selectedRequest))} className="px-2.5 py-1 text-uppercase fw-bold">
                                        {getOverallStatus(selectedRequest)}
                                    </Badge>
                                </div>
                                <div>
                                    <span className="fw-bold text-dark">Requested On: </span>
                                    <span>{selectedRequest.requested_date || moment(selectedRequest.created_at).format('YYYY-MM-DD')}</span>
                                </div>
                            </div>

                            <Row className="g-3 mb-3">
                                {(selectedRequest.holidays || selectedRequest.items || []).map((h: any, hIdx: number) => {
                                    const hDate = moment(h.HolidayDate || h.holiday_date, 'YYYY-MM-DD');
                                    const dayName = hDate.isValid() ? hDate.format('dddd') : (h.Day || '');
                                    const dateFormatted = hDate.isValid() ? hDate.format('D MMMM YYYY') : (h.HolidayDate || h.holiday_date);

                                    return (
                                        <Col md={6} key={h.HolidayId || hIdx}>
                                            <div className="p-3 rounded border text-center bg-light h-100 d-flex flex-column align-items-center justify-content-center" style={{ borderColor: '#e0e0e0' }}>
                                                <div className="fw-bold text-dark mb-2" style={{ fontSize: '1rem', color: '#000000' }}>
                                                    {dateFormatted}
                                                </div>
                                                {dayName && (
                                                    <Badge
                                                        className="px-3 py-1 mb-2 fw-bold text-white"
                                                        style={{ backgroundColor: getDayColor(dayName), borderRadius: '6px', fontSize: '0.82rem' }}
                                                    >
                                                        {dayName}
                                                    </Badge>
                                                )}
                                                <div className="fw-bold text-secondary">
                                                    {h.HolidayName || h.holiday_name || 'Optional Holiday'}
                                                </div>
                                            </div>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </>
                    )}
                </ModalBody>
                <ModalFooter className="border-top pt-3">
                    {selectedRequest && getOverallStatus(selectedRequest).toLowerCase() === 'pending' && !isOwnRequest(selectedRequest) && (
                        <div className="me-auto d-flex gap-2">
                            <Button
                                color="danger"
                                style={{ backgroundColor: '#EF5350', borderColor: '#EF5350' }}
                                className="fw-bold text-white"
                                onClick={() => handleOpenActionModal(selectedRequest, 'reject')}
                            >
                                Reject
                            </Button>
                            <Button
                                color="success"
                                style={{ backgroundColor: '#6cbb33', borderColor: '#6cbb33' }}
                                className="fw-bold text-white"
                                onClick={() => handleOpenActionModal(selectedRequest, 'approve')}
                            >
                                Approve
                            </Button>
                        </div>
                    )}
                    <Button color="secondary" onClick={() => setDetailModalOpen(false)}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Action Confirmation / Remark Modal */}
            <Modal isOpen={!!actionModalType} toggle={() => setActionModalType(null)} centered>
                <ModalHeader toggle={() => setActionModalType(null)}>
                    {actionModalType === 'approve' ? 'Approve Holiday Request' : 'Reject Holiday Request'}
                </ModalHeader>
                <ModalBody style={{ color: '#000000' }}>
                    {actionModalType === 'approve' ? (
                        <p className="fw-bold mb-0 text-dark">
                            Are you sure you want to approve this holiday request for {targetReqItem?.employee_name || 'this employee'}?
                        </p>
                    ) : (
                        <div>
                            <p className="fw-bold text-dark mb-2">
                                Reject holiday request for {targetReqItem?.employee_name || 'this employee'}?
                            </p>
                            <FormGroup>
                                <Label className="small fw-bold text-dark">Rejection Remark <span className="text-danger">*</span></Label>
                                <Input
                                    type="textarea"
                                    rows={3}
                                    placeholder="Enter rejection remark..."
                                    value={rejectRemark}
                                    onChange={(e) => setRejectRemark(e.target.value)}
                                    className="border text-dark shadow-none"
                                />
                            </FormGroup>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        color={actionModalType === 'approve' ? 'success' : 'danger'}
                        disabled={submittingAction}
                        style={{ backgroundColor: actionModalType === 'approve' ? '#6cbb33' : '#EF5350', borderColor: actionModalType === 'approve' ? '#6cbb33' : '#EF5350' }}
                        className="fw-bold text-white"
                        onClick={handleConfirmAction}
                    >
                        {submittingAction ? <Spinner size="sm" color="light" /> : (actionModalType === 'approve' ? 'Yes, Approve' : 'Reject Request')}
                    </Button>
                    <Button color="secondary" onClick={() => setActionModalType(null)} disabled={submittingAction}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}
